import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { IconUserPlus, IconCheck } from '@tabler/icons-react';
import { createFollowRequest, getFollowRequestBetweenUsers } from '../pages/auth/utils/firebase/userOperations';
import { useNotificationTriggers } from '../features/notifications';
import { showSuccessToast, showErrorToast } from '../utils/toastUtils';

/**
 * FollowBackButton component for notifications
 * Shows a "Follow Back" button when viewing a follow/follow_request_accepted notification
 * and the current user is not already following the actor.
 */
const FollowBackButton = ({ actorId, className = '' }) => {
    const currentUser = useSelector(state => state.auth.currentUser);
    const authUserData = useSelector(state => state.auth.userData);
    const { triggerFollowRequestNotification } = useNotificationTriggers();

    const [followStatus, setFollowStatus] = useState('checking'); // 'checking' | 'none' | 'requested' | 'following'
    const [loading, setLoading] = useState(false);

    // Check current follow status
    useEffect(() => {
        if (!currentUser?.uid || !actorId || currentUser.uid === actorId) {
            setFollowStatus('none');
            return;
        }

        // Check if already following
        const followingList = Array.isArray(authUserData?.following) ? authUserData.following : [];
        if (followingList.includes(actorId)) {
            setFollowStatus('following');
            return;
        }

        // Check for pending follow request
        const checkRequest = async () => {
            try {
                const req = await getFollowRequestBetweenUsers(currentUser.uid, actorId);
                if (req && req.status === 'pending') {
                    setFollowStatus('requested');
                } else {
                    setFollowStatus('none');
                }
            } catch (e) {
                console.warn('Failed to check follow request status:', e);
                setFollowStatus('none');
            }
        };

        checkRequest();
    }, [currentUser?.uid, actorId, authUserData?.following]);

    const handleFollowBack = useCallback(async (e) => {
        e.stopPropagation();

        if (!currentUser?.uid || !actorId || loading) return;

        try {
            setLoading(true);
            const request = await createFollowRequest(currentUser.uid, actorId);

            try {
                await triggerFollowRequestNotification(actorId, request.id);
            } catch (notifError) {
                console.error('Failed to send follow request notification:', notifError);
            }

            setFollowStatus('requested');
            showSuccessToast('Follow request sent!');
        } catch (error) {
            console.error('Error sending follow request:', error);
            showErrorToast('Failed to send follow request');
        } finally {
            setLoading(false);
        }
    }, [currentUser?.uid, actorId, loading, triggerFollowRequestNotification]);

    // Don't render if already following or still checking
    if (followStatus === 'following' || followStatus === 'checking') {
        return null;
    }

    // Show "Requested" state if request is pending
    if (followStatus === 'requested') {
        return (
            <button
                className={`follow-back-btn requested ${className}`}
                disabled
                onClick={(e) => e.stopPropagation()}
            >
                <IconCheck size={14} />
                <span>Cancel Request</span>
            </button>
        );
    }

    // Show "Follow Back" button
    return (
        <button
            className={`follow-back-btn ${className}`}
            onClick={handleFollowBack}
            disabled={loading}
        >
            <IconUserPlus size={14} />
            <span>{loading ? 'Sending...' : 'Follow Back'}</span>
        </button>
    );
};

export default FollowBackButton;
